import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { animate, motion, useMotionValue } from "framer-motion";
import { ChevronLeft, ChevronRight, Clock, FileText, CalendarDays, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const cards = [
  {
    id: 1,
    title: "Otra vez contra el reloj",
    description:
      "Tienes que analizar, preparar y entregar con la fecha encima. Solo de pensar por dónde empezar ya te agotas, y postergas. Imagina tener quien arranque por ti y te deje el borrador listo para revisar.",
    category: "Plazos",
    icon: Clock,
    image:
      "https://images.pexels.com/photos/8278873/pexels-photo-8278873.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: 2,
    title: "Vivo revisando lo mismo",
    description:
      "Documentar, ordenar, dar formato, revisar y volver a revisar. Tareas repetitivas que te comen el día y la energía. Un agente puede hacer el trabajo pesado y dejarte solo la decisión final.",
    category: "Documentos",
    icon: FileText,
    image:
      "https://images.unsplash.com/photo-1521791055366-0d553872125f?w=800&q=80",
  },
  {
    id: 3,
    title: "No me alcanza el día",
    description:
      "Reuniones, pendientes, recordatorios y todo en tu cabeza al mismo tiempo. Cuando reaccionas, ya se te pasó algo. Imagina un asistente que ordene tu agenda y no deje pasar ningún detalle.",
    category: "Agenda",
    icon: CalendarDays,
    image:
      "https://images.pexels.com/photos/6170652/pexels-photo-6170652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: 4,
    title: "Sé que la IA ayuda, pero no sé por dónde partir",
    description:
      "Todos hablan de inteligencia artificial y tú sientes que el tren se va sin ti. No es falta de ganas, es no saber dónde empezar. Para eso estoy: Te muestro, paso a paso, cómo empezar a usarla en lo que haces.",
    category: "IA",
    icon: Sparkles,
    image:
      "https://customer-assets.emergentagent.com/job_ia-concesiones-chile/artifacts/gxtcuv55_image.png",
  },
];

export function PainPointsSlider() {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const x = useMotionValue(0);

  useEffect(() => {
    if (containerRef.current) {
      setWidth(
        containerRef.current.scrollWidth - containerRef.current.offsetWidth
      );
    }
  }, []);

  const scrollTo = (direction) => {
    const currentX = x.get();
    const containerWidth = containerRef.current?.offsetWidth || 0;
    const scrollAmount = containerWidth * 0.8;

    let newX =
      direction === "left" ? currentX + scrollAmount : currentX - scrollAmount;

    newX = Math.max(Math.min(newX, 0), -width);

    animate(x, newX, {
      type: "spring",
      stiffness: 300,
      damping: 30,
      mass: 1,
    });
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4 relative group/slider">
      {/* Navigation Arrows */}
      <div className="absolute top-1/2 -translate-y-1/2 left-0 z-20 opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300">
        <button
          onClick={() => scrollTo("left")}
          data-testid="slider-prev-button"
          className="h-10 w-10 rounded-full bg-[#111111]/80 backdrop-blur-md border border-white/10 shadow-lg flex items-center justify-center hover:bg-[#111111] hover:scale-110 transition-all active:scale-95"
          aria-label="Desplazar a la izquierda"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
      </div>
      <div className="absolute top-1/2 -translate-y-1/2 right-0 z-20 opacity-0 group-hover/slider:opacity-100 transition-opacity duration-300">
        <button
          onClick={() => scrollTo("right")}
          data-testid="slider-next-button"
          className="h-10 w-10 rounded-full bg-[#111111]/80 backdrop-blur-md border border-white/10 shadow-lg flex items-center justify-center hover:bg-[#111111] hover:scale-110 transition-all active:scale-95"
          aria-label="Desplazar a la derecha"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      <motion.div
        ref={containerRef}
        className="cursor-grab active:cursor-grabbing overflow-hidden px-2 py-4 -mx-2 -my-4"
        whileTap={{ cursor: "grabbing" }}
      >
        <motion.div
          drag="x"
          dragConstraints={{ right: 0, left: -width }}
          dragElastic={0.1}
          style={{ x }}
          className="flex gap-6"
        >
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.id}
                className="min-w-[300px] max-w-[300px] sm:min-w-[340px] sm:max-w-[340px]"
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                data-testid={`pain-point-card-${card.id}`}
              >
                <Card className="group relative h-full overflow-hidden rounded-2xl border-white/5 bg-[#111111]/30 backdrop-blur-md transition-all duration-500 hover:border-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-500/10">
                  {/* Image Section */}
                  <div className="relative h-44 overflow-hidden">
                    <motion.img
                      src={card.image}
                      alt={card.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/30 to-transparent opacity-70 transition-opacity duration-300 group-hover:opacity-50" />

                    <div className="absolute top-3 left-3">
                      <Badge
                        className="bg-[#111111]/60 backdrop-blur-md border-white/10 text-cyan-400 text-xs font-medium px-3 py-1"
                      >
                        <Icon className="w-3 h-3 mr-1.5" />
                        {card.category}
                      </Badge>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-5">
                    <h3
                      className="text-base font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors"
                      style={{ fontFamily: "Fraunces, serif" }}
                    >
                      {card.title}
                    </h3>
                    <p className="text-base text-white/60 leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </motion.div>
    </div>
  );
}
